/**
 * اعلانِ مرورگر — نیمه‌ی عارضه‌دار. تصمیمِ «بپرسیم یا نه» در
 * `core/lib/webPushOffer.ts` است و آزموده می‌شود؛ این‌جا فقط APIهای مرورگر.
 *
 * ═══ چرا هیچ چیزِ تازه‌ای در سمتِ سرور لازم نبود ═══
 *
 * مرورگر هم دقیقاً مثلِ گوشی یک **توکنِ FCM** می‌گیرد. همان جدولِ
 * `push_tokens` (فقط با `platform: 'web'`)، همان تابعِ `notify-push`، و
 * همان `messages:send`. تنها فرقش این است که توکن از سرویس‌ورکر می‌آید نه
 * از سیستم‌عامل.
 *
 * ═══ ⚠ SDK فقط وقتی لود می‌شود که کاربر بله گفته باشد ═══
 *
 * `import()`ِ داینامیک عمدی است: کتابخانه‌ی فایربیس چند صد کیلوبایت است و
 * اکثرِ بازدیدکننده‌ها هرگز اعلان روشن نمی‌کنند. با این کار، بسته‌ی اصلی
 * اصلاً آن را حمل نمی‌کند و Vite خودش تکه‌ی جدا می‌سازد — همان قاعده‌ی
 * زبان‌ها در `core/lib/i18n.ts`.
 */
import { notifPrefsRepo } from '@/core/data/notifPrefsRepo';
import { reportError } from '@/core/lib/report';
import type { PushPermission } from '@/core/lib/webPushOffer';

/**
 * کلیدِ VAPID — گواهیِ Web Push پروژه، از کنسولِ فایربیس.
 *
 * ⚠ بدونش هیچ توکنی گرفته نمی‌شود، و این حالت **خطا نیست**: روی لوکال و
 * در تست‌های E2E عمداً خالی است و کلِ قابلیت بی‌صدا خاموش می‌ماند
 * (`shouldOfferPush` هم با `configured: false` کارت را نشان نمی‌دهد).
 */
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? '';

export const pushConfigured = (): boolean => VAPID_KEY.length > 0;

/**
 * کانفیگِ فایربیس — پیش‌فرض: پروژه‌ی پروداکشن (goout-f12c2). استیجینگ با
 * متغیرهای `VITE_FIREBASE_*` پروژه‌ی dev را جایگزین می‌کند، چون توکنِ مرورگر
 * و فرستنده‌ی سرور (تابعِ notify-push) باید از **یک** پروژه باشند — وگرنه
 * FCM با «SenderId mismatch» رد می‌کند و همه‌چیز سبز به نظر می‌رسد ولی هیچ
 * اعلانی نمی‌رسد. VAPID هم باید مالِ همان پروژه باشد.
 *
 * authDomain/storageBucket از projectId ساخته می‌شوند؛ هر دو پروژه همین
 * الگو را دارند و دو متغیرِ کم‌تر یعنی دو جای کم‌تر برای ناهماهنگی.
 */
const FB_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'goout-f12c2';
const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyCJEz8eeXLFHK_k0hxpQL8TjHlcVQtf92c',
  authDomain: `${FB_PROJECT_ID}.firebaseapp.com`,
  projectId: FB_PROJECT_ID,
  storageBucket: `${FB_PROJECT_ID}.firebasestorage.app`,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID || '343231456111',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:343231456111:web:06f51d9335b6cfce7f3e9a',
};

/**
 * سرویس‌ورکر از باندل رد نمی‌شود و env ندارد؛ جایگزینیِ پروژه از راهِ
 * کوئری‌استرینگِ همین نشانی به دستش می‌رسد (خودش پیش‌فرضِ پروداکشن دارد).
 * بدونِ جایگزینی، نشانی همان `/firebase-messaging-sw.js` سابق می‌ماند.
 */
const SW_URL = (() => {
  const q = new URLSearchParams();
  if (import.meta.env.VITE_FIREBASE_PROJECT_ID) q.set('projectId', import.meta.env.VITE_FIREBASE_PROJECT_ID);
  if (import.meta.env.VITE_FIREBASE_API_KEY) q.set('apiKey', import.meta.env.VITE_FIREBASE_API_KEY);
  if (import.meta.env.VITE_FIREBASE_SENDER_ID) q.set('senderId', import.meta.env.VITE_FIREBASE_SENDER_ID);
  if (import.meta.env.VITE_FIREBASE_APP_ID) q.set('appId', import.meta.env.VITE_FIREBASE_APP_ID);
  const s = q.toString();
  return `/firebase-messaging-sw.js${s ? `?${s}` : ''}`;
})();

/**
 * حالتِ اجازه، همان‌طور که مرورگر می‌گوید.
 *
 * ⚠ `unsupported` سه حالتِ کاملاً متفاوت را یکی می‌کند و این عمدی است،
 * چون واکنشِ ما به هر سه یکی است: مرورگرِ قدیمی، حالتِ ناشناس (که
 * سرویس‌ورکر ندارد)، و — مهم‌ترینش — **سافاریِ آیفون بدونِ افزودن به
 * صفحه‌ی خانه**. روی iOS اعلانِ وب فقط برای سایتی کار می‌کند که کاربر به
 * صفحه‌ی خانه اضافه کرده باشد؛ پس آن‌جا جوابِ درست «اپ را نصب کن» است، نه
 * یک دکمه‌ی بی‌اثر.
 */
export function pushPermission(): PushPermission {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  return Notification.permission as PushPermission;
}

/**
 * لودِ تنبلِ فایربیس + گرفتنِ messaging.
 *
 * ⚠ `initializeApp` بارِ دوم پرت می‌کند («app already exists») — پس هر که
 * فایربیس می‌خواهد از همین‌جا می‌گیرد و initِ تکراری همین یک‌جا مهار
 * می‌شود، نه در تک‌تکِ صدازننده‌ها.
 */
async function loadMessaging(registration: ServiceWorkerRegistration) {
  const [{ initializeApp, getApps, getApp }, mod] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  // ⚠ حتی با اجازه هم ممکن است نشود: `isSupported` چیزهایی را می‌بیند که
  // `Notification in window` نمی‌بیند.
  if (!(await mod.isSupported())) return null;
  const app = getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);
  const messaging = mod.getMessaging(app);
  return {
    getToken: () => mod.getToken(messaging, {
      vapidKey: VAPID_KEY, serviceWorkerRegistration: registration,
    }),
    deleteToken: () => mod.deleteToken(messaging),
  };
}

/**
 * می‌پرسد، و اگر اجازه داد توکن را می‌گیرد و ذخیره می‌کند.
 *
 * ⚠ ترتیب مهم است و برعکسش کار نمی‌کند: اول ثبتِ سرویس‌ورکر، بعد
 * `getToken`. خودِ `getToken` هم اگر سرویس‌ورکری ندهیم یکی پیدا می‌کند،
 * ولی آن‌وقت مسیرش را خودش حدس می‌زند و روی مسیرهای غیرِ ریشه می‌شکند.
 *
 * خروجی: `granted` یعنی از این به بعد اعلان می‌آید. هر چیز دیگری یعنی نه —
 * و `denied` یعنی دیگر هرگز نمی‌شود پرسید.
 */
export async function enableWebPush(userId: string): Promise<PushPermission> {
  if (!pushConfigured()) return 'unsupported';
  if (pushPermission() === 'unsupported') return 'unsupported';

  const decision = await Notification.requestPermission();
  if (decision !== 'granted') return decision as PushPermission;

  try {
    const registration = await navigator.serviceWorker.register(SW_URL);
    const fb = await loadMessaging(registration);
    if (!fb) return 'unsupported';

    const token = await fb.getToken();
    if (!token) return 'unsupported';

    await notifPrefsRepo.saveToken(userId, token, 'web');
    return 'granted';
  } catch (e) {
    // اجازه داده شده ولی توکن نگرفتیم — بدترین حالت برای سکوت، چون کاربر
    // فکر می‌کند روشن است. سطرِ خطا لازم است تا در لاگ دیده شود.
    reportError('push.web.enable', e);
    return 'unsupported';
  }
}

/**
 * «این مرورگر الان اعلان می‌گیرد؟» — بدونِ لود کردنِ فایربیس.
 *
 * ⚠ عمداً از `pushManager.getSubscription` می‌پرسد و نه از `getToken`: آن
 * یکی اگر اشتراکی نباشد یکی **می‌سازد** — یعنی چکِ وضعیت، اعلانِ
 * خاموش‌شده را بی‌صدا دوباره روشن می‌کرد.
 */
export async function webPushActive(): Promise<boolean> {
  if (pushPermission() !== 'granted') return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    const subscription = await registration?.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * خاموش کردن = **حذفِ توکن از دیتابیس** + لغوِ اشتراکِ مرورگر — نه فقط
 * بی‌صدایی. تا وقتی ردیف در `push_tokens` هست، فرستنده برای این مرورگر
 * تلاش می‌کند.
 *
 * ترتیب عمدی است: اول دیتابیس، بعد لغوِ محلی. اگر لغوِ محلی شکست بخورد،
 * ردیفی نمانده و اعلانی هم نمی‌رسد؛ ترتیبِ برعکس در بدترین حالت ردیفی به
 * جا می‌گذاشت که برای همیشه پوش می‌گرفت.
 */
export async function disableWebPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (!registration) return true;
    const fb = await loadMessaging(registration);
    if (!fb) return true;

    const token = await fb.getToken();
    if (token) await notifPrefsRepo.deleteToken(token);
    await fb.deleteToken();
    return true;
  } catch (e) {
    // شکستِ خاموش کردن باید دیده شود: کاربر فکر می‌کند خاموش است و نیست.
    reportError('push.web.disable', e);
    return false;
  }
}
